import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button.jsx';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import Stepper from './Stepper';
import { X, Plus } from 'lucide-react';
import { supabase, uploadDesignerAvatar, getImagePreview } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import './NewGameSetupModal.css';

const STEPS = [
  { id: 'basic', label: 'Game Info' },
  { id: 'sessions', label: 'Session Details' },
  { id: 'changes', label: 'Client Changes' }
];

const NewGameSetupModal = ({ open, onOpenChange, onSave, currentGame }) => {
  const { user } = useAuth();
  const formScrollRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);

  useEffect(() => {
    formScrollRef.current?.scrollTo(0, 0);
  }, [currentStep]);
  const [saving, setSaving] = useState(false);
  const [draftGameId, setDraftGameId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showCloseWarning, setShowCloseWarning] = useState(false);

  // Step 1: Basic Info
  const [gameName, setGameName] = useState('');
  const [designers, setDesigners] = useState([{ firstName: '', lastName: '', email: '', avatarUrl: null }]);
  const [designerAvatars, setDesignerAvatars] = useState([null]); // Store File objects for upload
  const [numberOfSessions, setNumberOfSessions] = useState(1);

  // Step 2: Session Details - Initialize with 1 session to match numberOfSessions default
  const [sessions, setSessions] = useState([
    {
      name: '',
      creativeBrief: '',
      targetAudience: '',
      requirements: [''],
      timerDuration: 15 // Default 15 minutes
    }
  ]);

  // Step 3: Client Changes - Default to 3 empty fields for sessionA (matching 1 session default)
  const [clientChanges, setClientChanges] = useState({
    sessionA: [
      { text: '', points: 1 },
      { text: '', points: 1 },
      { text: '', points: 1 }
    ]
  });

  // Step 3: Bonus Requirements - Separate field for bonus requirement
  const [bonusRequirements, setBonusRequirements] = useState({
    sessionA: { text: '', points: 4 }
  });


  // Function to save draft game
  const saveDraft = async (isStep1Complete = false) => {
    // Only save draft if Step 1 is completed (game name and at least one designer)
    if (!isStep1Complete) {
      if (!gameName.trim() || designers.filter(d => d.trim() !== '').length === 0) {
        return;
      }
    }

    if (!user?.id) {
      console.log('Cannot save draft: user not authenticated');
      return;
    }

    setSavingDraft(true);
    try {
      // Helper function to get full name from designer
      const getDesignerName = (d) => {
        if (d.firstName || d.lastName) {
          return `${d.firstName || ''} ${d.lastName || ''}`.trim();
        }
        return d.name || '';
      };

      // Upload designer avatars if needed
      const designersWithAvatars = await Promise.all(
        designers
          .filter(d => {
            const firstName = d.firstName || d.name?.split(' ')[0] || '';
            const lastName = d.lastName || d.name?.split(' ').slice(1).join(' ') || '';
            return firstName.trim() !== '' && lastName.trim() !== '';
          })
          .map(async (designer, index) => {
            const avatarFile = designerAvatars[index];
            const fullName = getDesignerName(designer);
            if (avatarFile && designer.avatarUrl && designer.avatarUrl.startsWith('blob:')) {
              try {
                // Upload avatar (use draftGameId if available, otherwise generate temp ID)
                const tempGameId = draftGameId || 'temp';
                const { url } = await uploadDesignerAvatar(avatarFile, fullName, tempGameId);
                return { 
                  firstName: designer.firstName?.trim() || designer.name?.split(' ')[0]?.trim() || '',
                  lastName: designer.lastName?.trim() || designer.name?.split(' ').slice(1).join(' ')?.trim() || '',
                  email: designer.email ? designer.email.trim() : '', 
                  avatarUrl: url 
                };
              } catch (error) {
                console.error('Error uploading avatar:', error);
                return { 
                  firstName: designer.firstName?.trim() || designer.name?.split(' ')[0]?.trim() || '',
                  lastName: designer.lastName?.trim() || designer.name?.split(' ').slice(1).join(' ')?.trim() || '',
                  email: designer.email ? designer.email.trim() : '', 
                  avatarUrl: null 
                };
              }
            }
            return {
              firstName: designer.firstName?.trim() || designer.name?.split(' ')[0]?.trim() || '',
              lastName: designer.lastName?.trim() || designer.name?.split(' ').slice(1).join(' ')?.trim() || '',
              email: designer.email ? designer.email.trim() : '',
              avatarUrl: designer.avatarUrl && !designer.avatarUrl.startsWith('blob:') ? designer.avatarUrl : null
            };
          })
      );

      const draftData = {
        game_name: gameName.trim() || 'Untitled Game',
        game_date: new Date().toISOString(),
        created_by: user.id,
        game_status: 'draft',
        designers: designersWithAvatars
      };

      let gameId = draftGameId;

      if (gameId) {
        // Update existing draft
        const { error } = await supabase
          .from('games')
          .update(draftData)
          .eq('id', gameId);
        
        if (error) {
          console.error('Error updating draft:', error);
        }
      } else {
        // Create new draft
        const { data, error } = await supabase
          .from('games')
          .insert([draftData])
          .select()
          .single();
        
        if (error) {
          console.error('Error creating draft:', error);
        } else if (data) {
          gameId = data.id;
          setDraftGameId(gameId);
        }
      }

      // Save session data if we have it
      if (gameId && currentStep >= 2) {
        await saveDraftSessions(gameId);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setSavingDraft(false);
    }
  };

  // Function to save draft sessions data
  const saveDraftSessions = async (gameId) => {
    try {
      // Delete existing draft sessions first
      await supabase
        .from('session_briefs')
        .delete()
        .eq('game_id', gameId);
      
      await supabase
        .from('session_requirements')
        .delete()
        .eq('game_id', gameId);

      // Save sessions
      for (let i = 0; i < numberOfSessions && i < sessions.length; i++) {
        const session = sessions[i];
        const sessionKey = i === 0 ? 'sessionA' : i === 1 ? 'sessionB' : `session${String.fromCharCode(67 + i)}`;
        
        // Save session brief
        await supabase
          .from('session_briefs')
          .insert([{
            game_id: gameId,
            session_name: sessionKey,
            session_display_name: session.name || '',
            creative_brief: session.creativeBrief || '',
            target_audience: session.targetAudience || '',
            timer_duration: session.timerDuration || 15
          }]);

        // Save requirements
        if (session.requirements && session.requirements.length > 0) {
          const requirementsToSave = session.requirements
            .filter(req => req && req.trim() !== '')
            .map(req => ({
              game_id: gameId,
              session_name: sessionKey,
              requirement_text: req.trim()
            }));

          if (requirementsToSave.length > 0) {
            await supabase
              .from('session_requirements')
              .insert(requirementsToSave);
          }
        }
      }
    } catch (error) {
      console.error('Error saving draft sessions:', error);
    }
  };

  // Function to save draft client changes
  const saveDraftClientChanges = async (gameId) => {
    try {
      // Delete existing client changes for this game
      await supabase
        .from('client_changes')
        .delete()
        .eq('game_id', gameId);

      // Save client changes
      for (const sessionKey of Object.keys(clientChanges)) {
        const changes = clientChanges[sessionKey];
        if (changes && changes.length > 0) {
          const changesToSave = changes
            .filter(change => change.text && change.text.trim() !== '')
            .map((change, index) => ({
              game_id: gameId,
              session_name: sessionKey,
              change_text: change.text.trim(),
              points: change.points || 1,
              is_bonus: false,
              order: index // Set order based on array position
            }));

          if (changesToSave.length > 0) {
            await supabase
              .from('client_changes')
              .insert(changesToSave);
          }
        }
      }

      // Save bonus requirements
      for (const sessionKey of Object.keys(bonusRequirements)) {
        const bonus = bonusRequirements[sessionKey];
        if (bonus && bonus.text && bonus.text.trim() !== '') {
            await supabase
              .from('client_changes')
              .insert([{
                game_id: gameId,
                session_name: sessionKey,
                change_text: bonus.text.trim(),
                points: bonus.points || 4,
                is_bonus: true,
                order: 9999 // Bonus changes always go at the end
              }]);
        }
      }
    } catch (error) {
      console.error('Error saving draft client changes:', error);
    }
  };

  // Helper function to reset form to blank state
  const resetForm = () => {
    setCurrentStep(1);
    setGameName('');
    setDesigners([{ firstName: '', lastName: '', email: '', avatarUrl: null }]);
    setDesignerAvatars([null]);
    setNumberOfSessions(1);
    // Initialize sessions based on numberOfSessions (which is 1)
    setSessions([
      {
        name: '',
        creativeBrief: '',
        targetAudience: '',
        requirements: [''],
        timerDuration: 15
      }
    ]);
    setClientChanges({
      sessionA: [
        { text: '', points: 1, timing: 2 },
        { text: '', points: 1, timing: 5 },
        { text: '', points: 1, timing: 8 }
      ]
    });
    setBonusRequirements({
      sessionA: { text: '', points: 4 }
    });
    setDraftGameId(null);
    setShowCloseWarning(false);
    setSaving(false); // Reset saving state when form is reset
  };

  // Check if user has unsaved changes
  const hasUnsavedChanges = () => {
    // Check if any step has been started
    if (currentStep > 1) return true;
    
    // Check if step 1 has any data
    if (gameName.trim() !== '') return true;
    
    const hasDesignerData = designers.some(d => {
      const firstName = d.firstName || '';
      const lastName = d.lastName || '';
      return firstName.trim() !== '' || lastName.trim() !== '' || d.email.trim() !== '' || d.avatarUrl;
    });
    
    if (hasDesignerData) return true;
    
    return false;
  };

  // Handle close with warning if needed
  const handleClose = () => {
    // If user hasn't completed all steps and has unsaved changes, show warning
    if (currentStep < STEPS.length && hasUnsavedChanges()) {
      setShowCloseWarning(true);
    } else {
      // No unsaved changes or completed all steps - close immediately
      resetForm();
      onOpenChange(false);
    }
  };

  // Confirm close - discard changes
  const handleConfirmClose = () => {
    resetForm();
    setShowCloseWarning(false);
    onOpenChange(false);
  };

  // Cancel close - stay in modal
  const handleCancelClose = () => {
    setShowCloseWarning(false);
  };

  // Reset form when modal opens - only load draft if explicitly editing via currentGame prop
  useEffect(() => {
    if (open) {
      // Reset saving state when modal opens
      setSaving(false);
      
      // Only load draft if currentGame is explicitly provided and it's a draft
      if (currentGame && currentGame.game_status === 'draft') {
        // Load the specific draft being edited
        setDraftGameId(currentGame.id);
        setGameName(currentGame.game_name || '');
        if (currentGame.designers && Array.isArray(currentGame.designers)) {
          const normalizedDesigners = currentGame.designers.length > 0 
            ? currentGame.designers.map(d => {
                if (typeof d === 'string') {
                  const nameParts = d.trim().split(' ');
                  const firstName = nameParts[0] || '';
                  const lastName = nameParts.slice(1).join(' ') || '';
                  return { firstName, lastName, email: '', avatarUrl: null };
                }
                if (d.name && !d.firstName) {
                  const nameParts = d.name.trim().split(' ');
                  const firstName = nameParts[0] || '';
                  const lastName = nameParts.slice(1).join(' ') || '';
                  return { firstName, lastName, email: d.email || '', avatarUrl: d.avatarUrl || null };
                }
                return { 
                  firstName: d.firstName || '', 
                  lastName: d.lastName || '', 
                  email: d.email || '', 
                  avatarUrl: d.avatarUrl || null 
                };
              })
            : [{ firstName: '', lastName: '', email: '', avatarUrl: null }];
          setDesigners(normalizedDesigners);
          setDesignerAvatars(new Array(normalizedDesigners.length).fill(null));
        } else {
          setDesigners([{ firstName: '', lastName: '', email: '', avatarUrl: null }]);
          setDesignerAvatars([null]);
        }
      } else {
        // Starting fresh - reset everything to blank
        resetForm();
      }
    } else {
      // Modal closed - reset form
      resetForm();
    }
  }, [open, currentGame?.id]);

  // Update sessions when numberOfSessions changes
  useEffect(() => {
    if (numberOfSessions < sessions.length) {
      setSessions(sessions.slice(0, numberOfSessions));
      // Also update clientChanges and bonusRequirements
      const newChanges = { ...clientChanges };
      const newBonus = { ...bonusRequirements };
      if (numberOfSessions === 1) {
        delete newChanges.sessionB;
        delete newBonus.sessionB;
      }
      setClientChanges(newChanges);
      setBonusRequirements(newBonus);
    } else if (numberOfSessions > sessions.length) {
      const newSessions = [...sessions];
      for (let i = sessions.length; i < numberOfSessions; i++) {
        newSessions.push({
          name: '',
          creativeBrief: '',
          targetAudience: '',
          requirements: [''],
          timerDuration: 15
        });
        // Add 3 default client changes for new session
        const sessionKey = i === 0 ? 'sessionA' : i === 1 ? 'sessionB' : `session${String.fromCharCode(67 + i)}`;
        setClientChanges(prev => ({
          ...prev,
          [sessionKey]: [
            { text: '', points: 1, timing: 2 },
            { text: '', points: 1, timing: 5 },
            { text: '', points: 1, timing: 8 }
          ]
        }));
        setBonusRequirements(prev => ({
          ...prev,
          [sessionKey]: { text: '', points: 4, timing: 10 }
        }));
      }
      setSessions(newSessions);
    }
  }, [numberOfSessions]);

  // Auto-resize requirement textareas when they're rendered or value changes
  useEffect(() => {
    if (currentStep === 2) {
      const textareas = document.querySelectorAll('.requirement-input-row textarea');
      textareas.forEach(textarea => {
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
      });
    }
  }, [currentStep, sessions]);

  const handleAddDesigner = () => {
    setDesigners([...designers, { firstName: '', lastName: '', email: '', avatarUrl: null }]);
    setDesignerAvatars([...designerAvatars, null]);
  };

  const handleRemoveDesigner = (index) => {
    // Don't allow removing if there's only one designer field
    if (designers.length > 1) {
      const newDesigners = designers.filter((_, i) => i !== index);
      const newAvatars = designerAvatars.filter((_, i) => i !== index);
      setDesigners(newDesigners);
      setDesignerAvatars(newAvatars);
    }
  };

  const handleDesignerFirstNameChange = (index, value) => {
    const newDesigners = [...designers];
    newDesigners[index] = { ...newDesigners[index], firstName: value };
    setDesigners(newDesigners);
  };

  const handleDesignerLastNameChange = (index, value) => {
    const newDesigners = [...designers];
    newDesigners[index] = { ...newDesigners[index], lastName: value };
    setDesigners(newDesigners);
  };

  const handleDesignerEmailChange = (index, value) => {
    const newDesigners = [...designers];
    newDesigners[index] = { ...newDesigners[index], email: value };
    setDesigners(newDesigners);
  };

  const handleDesignerAvatarChange = async (index, file) => {
    if (!file) return;
    
    try {
      const preview = await getImagePreview(file);
      const newDesigners = [...designers];
      const newAvatars = [...designerAvatars];
      
      // Store preview URL temporarily and file for upload later
      newDesigners[index] = { ...newDesigners[index], avatarUrl: preview };
      newAvatars[index] = file;
      
      setDesigners(newDesigners);
      setDesignerAvatars(newAvatars);
    } catch (error) {
      console.error('Error previewing image:', error);
      alert('Failed to load image preview');
    }
  };

  const handleAddRequirement = (sessionIndex) => {
    const newSessions = [...sessions];
    newSessions[sessionIndex].requirements.push('');
    setSessions(newSessions);
  };

  const handleRemoveRequirement = (sessionIndex, reqIndex) => {
    // Don't allow removing if there's only one requirement field
    const newSessions = [...sessions];
    if (newSessions[sessionIndex].requirements.length > 1) {
      newSessions[sessionIndex].requirements = newSessions[sessionIndex].requirements.filter((_, i) => i !== reqIndex);
      setSessions(newSessions);
    }
  };

  const handleRequirementChange = (sessionIndex, reqIndex, value) => {
    const newSessions = [...sessions];
    newSessions[sessionIndex].requirements[reqIndex] = value;
    setSessions(newSessions);
  };

  const handleAddClientChange = (sessionKey) => {
    const newChanges = { ...clientChanges };
    if (!newChanges[sessionKey]) {
      newChanges[sessionKey] = [];
    }
    newChanges[sessionKey].push({
      text: '',
      points: 1
    });
    setClientChanges(newChanges);
  };

  const handleBonusRequirementUpdate = (sessionKey, field, value) => {
    setBonusRequirements(prev => ({
      ...prev,
      [sessionKey]: {
        ...prev[sessionKey],
        [field]: value
      }
    }));
  };

  const handleRemoveClientChange = (sessionKey, changeIndex) => {
    const newChanges = { ...clientChanges };
    newChanges[sessionKey] = newChanges[sessionKey].filter((_, i) => i !== changeIndex);
    setClientChanges(newChanges);
  };

  const handleClientChangeUpdate = (sessionKey, changeIndex, field, value) => {
    const newChanges = { ...clientChanges };
    newChanges[sessionKey][changeIndex] = {
      ...newChanges[sessionKey][changeIndex],
      [field]: value
    };
    setClientChanges(newChanges);
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        // Designer information is now optional
        return gameName.trim() !== '';
      case 2:
        return sessions.every(s => 
          s.name.trim() !== '' && 
          s.creativeBrief.trim() !== '' && 
          s.targetAudience.trim() !== '' &&
          s.requirements.length > 0 &&
          s.requirements.some(r => r.trim() !== '')
        );
      case 3:
        // Only validate sessions that exist based on numberOfSessions
        const sessionKeysToValidate = [];
        for (let i = 0; i < numberOfSessions; i++) {
          const sessionKey = i === 0 ? 'sessionA' : i === 1 ? 'sessionB' : `session${String.fromCharCode(67 + i)}`;
          sessionKeysToValidate.push(sessionKey);
        }
        
        return sessionKeysToValidate.every(sessionKey => {
          const changes = clientChanges[sessionKey] || [];
          // Require exactly 3 valid client changes (all must be filled out)
          if (changes.length < 3) {
            console.log(`Step 3 validation failed: Only ${changes.length} changes found for ${sessionKey}, need 3`);
            return false;
          }
          
          // All 3 changes must be valid (have text and valid points)
          const firstThreeChanges = changes.slice(0, 3);
          const allValid = firstThreeChanges.every((change, index) => {
            const textValid = change.text && change.text.trim() !== '';
            const pointsValid = change.points && typeof change.points === 'number' && change.points > 0;
            if (!textValid) {
              console.log(`Step 3 validation failed: Change ${index + 1} for ${sessionKey} has empty text`);
            }
            if (!pointsValid) {
              console.log(`Step 3 validation failed: Change ${index + 1} for ${sessionKey} has invalid points:`, change.points);
            }
            return textValid && pointsValid;
          });
          
          if (!allValid) {
            console.log(`Step 3 validation failed for ${sessionKey}. Changes:`, firstThreeChanges);
          }
          
          return allValid;
        });
      default:
        return true;
    }
  };

  const handleNext = async () => {
    if (validateStep(currentStep)) {
      // No auto-save during steps - only save when completing all 3 steps
      setCurrentStep(currentStep + 1);
      // Don't save draft - only save when completing all steps
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleStepClick = (stepNumber) => {
    // Only allow clicking on completed steps or current step
    if (stepNumber <= currentStep) {
      setCurrentStep(stepNumber);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(currentStep)) return;

    // Set saving state immediately so button text updates
    setSaving(true);
    
    // Small delay to ensure state update is visible before async operations
    await new Promise(resolve => setTimeout(resolve, 50));
    
    try {
      // Helper function to get full name from designer
      const getDesignerName = (d) => {
        if (d.firstName || d.lastName) {
          return `${d.firstName || ''} ${d.lastName || ''}`.trim();
        }
        return d.name || '';
      };

      // Upload designer avatars before finalizing
      // Map all designers with their indices, then filter to only valid ones
      // This preserves the index mapping for avatar files
      const designersWithIndices = designers.map((designer, index) => ({ designer, index }));
      const validDesignersWithIndices = designersWithIndices.filter(({ designer }) => {
        const firstName = designer.firstName || designer.name?.split(' ')[0] || '';
        const lastName = designer.lastName || designer.name?.split(' ').slice(1).join(' ') || '';
        return firstName.trim() !== '' && lastName.trim() !== '';
      });
      
      // Map valid designers with their original indices to get correct avatar files
      // Email and photo are optional - include designer even without them
      const designersWithAvatars = await Promise.all(
        validDesignersWithIndices.map(async ({ designer, index: originalIndex }) => {
          const avatarFile = designerAvatars[originalIndex];
          const fullName = getDesignerName(designer);
          
          if (avatarFile && designer.avatarUrl && designer.avatarUrl.startsWith('blob:')) {
              try {
                // Use draftGameId if available, otherwise use a temp ID (will be replaced when game is created)
                const tempGameId = draftGameId || 'temp';
                const { url } = await uploadDesignerAvatar(avatarFile, fullName, tempGameId);
                return { 
                  firstName: designer.firstName?.trim() || designer.name?.split(' ')[0]?.trim() || '',
                  lastName: designer.lastName?.trim() || designer.name?.split(' ').slice(1).join(' ')?.trim() || '',
                  email: designer.email ? designer.email.trim() : '',
                  avatarUrl: url 
                };
              } catch (error) {
                console.error('Error uploading avatar:', error);
                return { 
                  firstName: designer.firstName?.trim() || designer.name?.split(' ')[0]?.trim() || '',
                  lastName: designer.lastName?.trim() || designer.name?.split(' ').slice(1).join(' ')?.trim() || '',
                  email: designer.email ? designer.email.trim() : '',
                  avatarUrl: null 
                };
              }
            }
            return {
              firstName: designer.firstName?.trim() || designer.name?.split(' ')[0]?.trim() || '',
              lastName: designer.lastName?.trim() || designer.name?.split(' ').slice(1).join(' ')?.trim() || '',
              email: designer.email ? designer.email.trim() : '',
              avatarUrl: designer.avatarUrl && !designer.avatarUrl.startsWith('blob:') ? designer.avatarUrl : null
            };
          })
      );

      // Save game when completing all 3 steps - this creates the game that will appear in "My Games"
      // Only save now, not during intermediate steps
      if (!draftGameId) {
        // Create new game (will be saved via onSave callback)
        // No need to create draft first since we're completing all steps
      } else {
        // If there was a draft, we'll update it via onSave callback
      }

      // Transform data for the onSave callback
      const gameData = {
        gameName: gameName.trim(),
        designers: designersWithAvatars,
        numberOfSessions,
        sessions: sessions.map((session, index) => ({
          ...session,
          sessionKey: index === 0 ? 'sessionA' : index === 1 ? 'sessionB' : `session${String.fromCharCode(67 + index)}`
        })),
        clientChanges: Object.keys(clientChanges).map(sessionKey => ({
          sessionKey,
          changes: clientChanges[sessionKey]
        })),
        bonusRequirements: Object.keys(bonusRequirements).map(sessionKey => ({
          sessionKey,
          ...bonusRequirements[sessionKey]
        })),
        draftGameId: draftGameId // Pass draft ID so App.js can update it instead of creating new
      };


      // Call onSave and wait for it to complete (including navigation)
      // Keep modal open and button showing "Creating game..." until everything is done
      await onSave(gameData);
      
      // onSave will handle closing modal and navigation
      // Reset saving state and form after successful save
      setSaving(false);
      setDraftGameId(null);
      resetForm();
    } catch (error) {
      console.error('Error saving game:', error);
      alert(`Failed to save game: ${error.message || 'Please try again.'}`);
      // Reset saving state so user can try again
      setSaving(false);
      // Don't clear draft ID on error - allow user to retry
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content step-content--with-image">
            <div className="cc-step1-card">
              <div className="cc-step1-image">
              <div
                className="cc-step1-image-inner"
                style={{
                  background: "url('/images/find-image-coaster-bg.png') center top/auto 100% no-repeat",
                  overflow: 'hidden',
                  position: 'relative',
                  width: '100%',
                  minHeight: '200px'
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(180deg, rgba(6,0,16,0.2) 0%, rgba(6,0,16,0.75) 100%)'
                  }}
                />
              </div>
              </div>
              <div className="cc-step1-fields">
              <div className="step-field">
                <Label htmlFor="gameName">Game Name *</Label>
                <Input
                  id="gameName"
                  type="text"
                  placeholder="Name your game"
                  value={gameName}
                  onChange={(e) => setGameName(e.target.value)}
                  required
                />
              </div>

              <div className="step-field">
                <Label>Number of Sessions *</Label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="numberOfSessions"
                      value="1"
                      checked={numberOfSessions === 1}
                      onChange={(e) => setNumberOfSessions(1)}
                    />
                    <span>1 Session</span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="numberOfSessions"
                      value="2"
                      checked={numberOfSessions === 2}
                      onChange={(e) => setNumberOfSessions(2)}
                    />
                    <span>2 Sessions</span>
                  </label>
                </div>
              </div>

              <div className="step-field">
                <Label>Designers</Label>
                <div className="designers-list">
                  {designers.map((designer, index) => (
                    <div key={index} className="designer-input-row" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', width: '100%' }}>
                      <div style={{ flex: '0 0 140px' }}>
                        <Input
                          type="text"
                          placeholder="First name"
                          value={designer.firstName || ''}
                          onChange={(e) => handleDesignerFirstNameChange(index, e.target.value)}
                        />
                      </div>
                      <div style={{ flex: '0 0 140px' }}>
                        <Input
                          type="text"
                          placeholder="Last name"
                          value={designer.lastName || ''}
                          onChange={(e) => handleDesignerLastNameChange(index, e.target.value)}
                        />
                      </div>
                      <div style={{ flex: '1 1 180px', minWidth: '120px' }}>
                        <Input
                          type="email"
                          placeholder="Email (optional)"
                          value={designer.email || ''}
                          onChange={(e) => handleDesignerEmailChange(index, e.target.value)}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
                        <label
                          htmlFor={`designer-avatar-${index}`}
                          style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            border: '2px solid rgba(255, 255, 255, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            background: designer.avatarUrl && !designer.avatarUrl.startsWith('blob:') 
                              ? `url(${designer.avatarUrl}) center/cover`
                              : designer.avatarUrl 
                              ? `url(${designer.avatarUrl}) center/cover`
                              : 'rgba(255, 255, 255, 0.05)',
                            overflow: 'hidden',
                            flexShrink: 0
                          }}
                        >
                          {!designer.avatarUrl && (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" style={{ width: '20px', height: '20px', color: 'rgba(255, 255, 255, 0.5)' }}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM18.75 10.5h.008v.008h-.008V10.5Z" />
                            </svg>
                          )}
                        </label>
                        <input
                          id={`designer-avatar-${index}`}
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleDesignerAvatarChange(index, file);
                            }
                          }}
                        />
                        {designers.length > 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleRemoveDesigner(index)}
                            className="remove-button"
                            style={{ 
                              width: '48px', 
                              height: '48px', 
                              padding: 0, 
                              flexShrink: 0,
                              position: 'relative',
                              right: 'auto',
                              top: 'auto',
                              transform: 'none'
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6" style={{ width: '16px', height: '16px' }}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                            </svg>
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleAddDesigner}
                    className="add-designer-button"
                  >
                    <Plus size={16} />
                    Add Designer
                  </button>
                </div>
              </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className={`step-content ${sessions.length === 1 ? 'step-content--single-session' : ''}`}>
            {sessions.map((session, sessionIndex) => (
              <div key={sessionIndex} className="session-section">
                <h3 className="session-title">Session {sessionIndex + 1}</h3>
                
                <div className="step-field">
                  <Label htmlFor={`session-name-${sessionIndex}`}>Session Name *</Label>
                  <Input
                    id={`session-name-${sessionIndex}`}
                    type="text"
                    placeholder="Enter session name"
                    value={session.name}
                    onChange={(e) => {
                      const newSessions = [...sessions];
                      newSessions[sessionIndex].name = e.target.value;
                      setSessions(newSessions);
                    }}
                    required
                  />
                </div>

                <div className="step-field">
                  <Label htmlFor={`creative-brief-${sessionIndex}`}>Creative Brief *</Label>
                  <Textarea
                    id={`creative-brief-${sessionIndex}`}
                    placeholder="Describe the creative brief for this session..."
                    value={session.creativeBrief}
                    onChange={(e) => {
                      const newSessions = [...sessions];
                      newSessions[sessionIndex].creativeBrief = e.target.value;
                      setSessions(newSessions);
                    }}
                    rows={4}
                    required
                  />
                </div>

                <div className="step-field">
                  <Label htmlFor={`target-audience-${sessionIndex}`}>Target Audience *</Label>
                  <Textarea
                    id={`target-audience-${sessionIndex}`}
                    placeholder="Describe the target audience..."
                    value={session.targetAudience}
                    onChange={(e) => {
                      const newSessions = [...sessions];
                      newSessions[sessionIndex].targetAudience = e.target.value;
                      setSessions(newSessions);
                    }}
                    rows={3}
                    required
                  />
                </div>

                <div className="step-field">
                  <Label htmlFor={`timer-${sessionIndex}`}>Timer Duration *</Label>
                  <div className="radio-group">
                    {[15, 20, 25, 30].map((minutes) => (
                      <label key={minutes} className="radio-option">
                        <input
                          type="radio"
                          name={`timer-${sessionIndex}`}
                          value={minutes}
                          checked={session.timerDuration === minutes}
                          onChange={(e) => {
                            const newSessions = [...sessions];
                            newSessions[sessionIndex].timerDuration = parseInt(e.target.value);
                            setSessions(newSessions);
                          }}
                        />
                        <span>{minutes} min</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="step-field">
                  <Label>Initial Requirements *</Label>
                  <p className="field-hint">Add at least one requirement</p>
                  <div className="requirements-list">
                    {session.requirements.map((req, reqIndex) => (
                      <div key={reqIndex} className="requirement-input-row">
                        <Textarea
                          placeholder="Requirement"
                          value={req}
                          onChange={(e) => {
                            handleRequirementChange(sessionIndex, reqIndex, e.target.value);
                            // Auto-resize textarea
                            e.target.style.height = 'auto';
                            e.target.style.height = e.target.scrollHeight + 'px';
                          }}
                          className="auto-resize-textarea"
                        />
                        {session.requirements.length > 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleRemoveRequirement(sessionIndex, reqIndex)}
                            className="remove-button"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" style={{ width: '16px', height: '16px' }}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                            </svg>
                          </Button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => handleAddRequirement(sessionIndex)}
                      className="add-designer-button"
                    >
                      <Plus size={16} />
                      Add Requirement
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 3:
        return (
          <div className={`step-content ${sessions.length === 1 ? 'step-content--single-session' : ''}`}>
            {sessions.map((session, sessionIndex) => {
              const sessionKey = sessionIndex === 0 ? 'sessionA' : sessionIndex === 1 ? 'sessionB' : `session${String.fromCharCode(67 + sessionIndex)}`;
              const changes = clientChanges[sessionKey] || [];

              return (
                <div key={sessionIndex} className="session-section">
                  <h3 className="session-title">{session.name} - Client Changes</h3>
                  
                  <div className="client-changes-list">
                    {changes.map((change, changeIndex) => (
                            <div key={changeIndex} className="client-change-card">
                              <div className="step-field">
                                <Label>Client Change *</Label>
                                <Textarea
                                  placeholder="Describe the client change..."
                                  value={change.text}
                                  onChange={(e) => handleClientChangeUpdate(sessionKey, changeIndex, 'text', e.target.value)}
                                  rows={3}
                                  required
                                />
                              </div>

                        <div className="client-change-options">
                          <div className="step-field">
                            <Label htmlFor={`points-${sessionIndex}-${changeIndex}`}>Points *</Label>
                            <Input
                              id={`points-${sessionIndex}-${changeIndex}`}
                              type="number"
                              min="1"
                              max="10"
                              value={change.points}
                              onChange={(e) => handleClientChangeUpdate(sessionKey, changeIndex, 'points', parseInt(e.target.value) || 1)}
                              required
                            />
                          </div>

                          {changes.length > 3 && (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => handleRemoveClientChange(sessionKey, changeIndex)}
                              className="remove-button"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" style={{ width: '16px', height: '16px' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                              </svg>
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={() => handleAddClientChange(sessionKey)}
                      className="add-designer-button"
                    >
                      <Plus size={16} />
                      Add Client Change
                    </button>

                    {/* Bonus Client Change Field */}
                    <div className="bonus-requirement-section">
                      <div className="step-field">
                        <Label>Bonus Client Change</Label>
                        <Textarea
                          placeholder="Describe the bonus client change..."
                          value={bonusRequirements[sessionKey]?.text || ''}
                          onChange={(e) => handleBonusRequirementUpdate(sessionKey, 'text', e.target.value)}
                          rows={3}
                        />
                      </div>
                      <div className="client-change-options">
                        <div className="step-field">
                          <Label htmlFor={`bonus-points-${sessionIndex}`}>Bonus Points</Label>
                          <Input
                            id={`bonus-points-${sessionIndex}`}
                            type="number"
                            min="1"
                            max="10"
                            value={bonusRequirements[sessionKey]?.points || 4}
                            onChange={(e) => handleBonusRequirementUpdate(sessionKey, 'points', parseInt(e.target.value) || 4)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* Warning Dialog */}
      {showCloseWarning && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 10002,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              backgroundColor: '#060010',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '400px',
              width: '90%',
              color: '#ffffff'
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.25rem', fontWeight: '600' }}>
              Unsaved Changes
            </h3>
            <p style={{ marginBottom: '1.5rem', color: 'rgba(255, 255, 255, 0.7)' }}>
              You haven't completed all steps yet. If you exit now, you will lose all the information you've entered. Are you sure you want to continue?
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelClose}
                style={{ color: '#ffffff', borderColor: 'rgba(255, 255, 255, 0.3)' }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleConfirmClose}
                style={{ backgroundColor: '#dc2626', color: '#ffffff' }}
              >
                Exit Without Saving
              </Button>
            </div>
          </div>
        </div>
      )}
      {open && (
        <div
          className="new-game-overlay"
          onClick={handleClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            zIndex: 10000,
            cursor: 'pointer'
          }}
        />
      )}
      <Dialog open={open} onOpenChange={(isOpen) => {
        if (!isOpen) {
          handleClose();
        }
      }}>
        <DialogContent className="new-game-modal new-game-modal--fullscreen">
          <button
            type="button"
            className="new-game-close-button"
            onClick={handleClose}
            aria-label="Close"
          >
            <X size={20} />
          </button>

          <DialogHeader>
            <DialogTitle className="new-game-title">Set Up New Creative Coaster Game</DialogTitle>
            <DialogDescription className="new-game-description">
              Follow the steps below to create your design challenge game
            </DialogDescription>
          </DialogHeader>

          <Stepper 
            step={currentStep}
            onStepChange={(s) => handleStepClick(s)}
            hideFooter={true}
            stepCircleContainerClassName="new-game-stepper-indicators"
            stepContainerClassName="new-game-stepper-row"
          >
            {STEPS.map((s) => <div key={s.id} aria-hidden="true" style={{ minHeight: 0, overflow: 'hidden' }} />)}
          </Stepper>

          {/* Fixed blur overlay at bottom of modal - blurs content scrolling behind action bar */}
          <div className="new-game-footer-blur-overlay" />

          <form onSubmit={handleSubmit} className="new-game-form">
            <div ref={formScrollRef} className="new-game-form-scroll">
              {renderStepContent()}
            </div>

            <div className="new-game-actions">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={handleBack}
                    disabled={saving}
                    className="new-game-back-button"
                  >
                    Back
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {currentStep < STEPS.length ? (
                  <Button
                    type="button"
                    onClick={handleNext}
                    disabled={!validateStep(currentStep)}
                    className="new-game-primary-button"
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={saving || !validateStep(currentStep)}
                    className="new-game-primary-button"
                  >
                    {saving ? 'Creating game...' : 'Create Game'}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NewGameSetupModal;
